import { Dispatch } from 'redux'
import * as R from 'ramda'

import { Operation } from './operationHelper'
import { FirstArg, FirstReturn, ServiceResponseError } from './typeHelpers'

type OperationSuccessResult<TData> = {
  ok: true,
  data: TData,
  error: null
}
type OperationErrorResult = {
  ok: false,
  data: null,
  error: ServiceResponseError
}
type OperationResult<TData> = OperationSuccessResult<TData> | OperationErrorResult

type ApiOperation<TParams, TData> = TParams extends object ? (params: TParams) => Operation<TData> : () => Operation<TData>
export type ConnectedApiOperation<TParams, TData> = TParams extends object ? (params: TParams) => Promise<OperationResult<TData>>: () => Promise<OperationResult<TData>>
type TypeOfOperation<Op> = Op extends (dispatch: any) => Promise<infer TData | null> ? TData : any
const connectApiOperation = (dispatch: Dispatch<any>) => <TParams, TData>(apiOperation: ApiOperation<TParams, TData>): ConnectedApiOperation<TParams, TData> => {
  const connectedApiOperation = async (params: TParams): Promise<OperationResult<TData>> => {
    try {
      const dispatchResult = dispatch(apiOperation(params as any)) as unknown as Promise<any>
      const data = await dispatchResult
      return {
        data,
        error: null,
        ok: true
      }
    } catch (err) {
      return {
        data: null,
        error: err,
        ok: false
      }
    }
  }
  return connectedApiOperation as any
}

export const useApiDispatch = <T extends { [K: string]: ApiOperation<any, any> }>(apiOperationMapper: T, dispatch: Dispatch<any>): { [K in keyof T]: ConnectedApiOperation<FirstArg<T[K]>, TypeOfOperation<FirstReturn<T[K]>>> } => {
  return R.map(connectApiOperation(dispatch) as any, apiOperationMapper)
}
